import apiService from './api'

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null
  private publicKey: string | null = null

  // Inicializar Service Worker e obter chave pública
  async initialize(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Workers não suportados')
      return false
    }

    if (!('PushManager' in window)) {
      console.warn('Push Notifications não suportadas')
      return false
    }

    try {
      // Registrar Service Worker
      this.registration = await navigator.serviceWorker.register('/sw.js')
      console.log('✅ Service Worker registrado:', this.registration)

      // Obter chave pública VAPID
      const response = await apiService.get<{ publicKey: string }>('/push/public-key')
      this.publicKey = response.data?.publicKey || null
      console.log('✅ Chave pública VAPID obtida')

      return true
    } catch (error) {
      console.error('❌ Erro ao inicializar push service:', error)
      return false
    }
  }

  // Verificar se permissão foi concedida
  hasPermission(): boolean {
    return Notification.permission === 'granted'
  }

  // Solicitar permissão
  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Notificações não suportadas')
      return false
    }

    if (Notification.permission === 'granted') {
      console.log('✅ Permissão de notificações já concedida')
      return true
    }

    console.log('🔔 Solicitando permissão de notificações...')
    const permission = await Notification.requestPermission()
    console.log('🔔 Resposta de permissão:', permission)
    
    return permission === 'granted'
  }

  // Subscrever a push notifications
  async subscribe(): Promise<boolean> {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    console.log('📱 [PUSH SUBSCRIBE] Iniciando subscrição...')
    
    // Verificar token ANTES de tudo
    const token = localStorage.getItem('token')
    console.log('🔑 [PUSH SUBSCRIBE] Token no localStorage:', token ? `${token.substring(0, 20)}...` : 'NENHUM TOKEN!')
    
    if (!token) {
      console.error('❌ [PUSH SUBSCRIBE] Token não encontrado no localStorage!')
      return false
    }

    if (!this.registration || !this.publicKey) {
      console.log('📱 [PUSH SUBSCRIBE] Inicializando Service Worker...')
      await this.initialize()
    }

    if (!this.registration || !this.publicKey) {
      console.error('❌ [PUSH SUBSCRIBE] Service Worker ou chave pública não disponíveis')
      return false
    }

    try {
      // Verificar se já existe subscrição
      let subscription = await this.registration.pushManager.getSubscription()

      // Se não existe, criar nova
      if (!subscription) {
        console.log('📱 [PUSH SUBSCRIBE] Criando nova subscrição push...')
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.publicKey)
        })
        console.log('✅ [PUSH SUBSCRIBE] Subscrição criada:', subscription.endpoint.substring(0, 50) + '...')
      } else {
        console.log('ℹ️  [PUSH SUBSCRIBE] Subscrição já existente:', subscription.endpoint.substring(0, 50) + '...')
      }

      const subscriptionJSON = subscription.toJSON()
      console.log('📤 [PUSH SUBSCRIBE] Enviando subscrição para backend...')
      console.log('📤 [PUSH SUBSCRIBE] URL:', '/push/subscribe')
      console.log('📤 [PUSH SUBSCRIBE] Token será enviado via interceptor')

      // IMPORTANTE: Usar axios diretamente com token no header como fallback
      const axiosInstance = apiService.getAxiosInstance()
      
      // Verificar token novamente EXATAMENTE antes da requisição
      const tokenAgain = localStorage.getItem('token')
      console.log('🔑 [PUSH SUBSCRIBE] Token IMEDIATAMENTE antes da requisição:', tokenAgain ? `${tokenAgain.substring(0, 20)}...` : 'NENHUM!')

      const response = await axiosInstance.post('/push/subscribe', 
        { subscription: subscriptionJSON },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      console.log('✅ [PUSH SUBSCRIBE] Resposta do backend:', response.status, response.data)
      console.log('✅ [PUSH SUBSCRIBE] Subscrição registrada com sucesso!')
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
      return true
    } catch (error: any) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
      console.error('❌ [PUSH SUBSCRIBE] Erro ao subscrever:', error)
      console.error('❌ [PUSH SUBSCRIBE] Status:', error.response?.status)
      console.error('❌ [PUSH SUBSCRIBE] Mensagem:', error.response?.data)
      console.error('❌ [PUSH SUBSCRIBE] Headers enviados:', error.config?.headers)
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
      return false
    }
  }

  // Cancelar subscrição
  async unsubscribe(): Promise<boolean> {
    if (!this.registration) {
      return false
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription()

      if (subscription) {
        await subscription.unsubscribe()

        // Remover do backend
        await apiService.post('/push/unsubscribe', {
          endpoint: subscription.endpoint
        })

        console.log('✅ Subscrição removida com sucesso')
      }

      return true
    } catch (error) {
      console.error('❌ Erro ao cancelar subscrição:', error)
      return false
    }
  }

  // Converter chave base64 para Uint8Array
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }
}

export default new PushNotificationService()
